'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { isNsfw, isPolitics, isTroll } from '@shared/all/domains/posts';
import { SpaceMemberRoles } from '@shared/all/domains/spaces';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useNavigator from '@shared/utils/hooks/useNavigator';
import useSocket from '@shared/utils/hooks/useSocket';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import useOnScreen from '../../hooks/useOnScreen';
import { client } from '../../utils/trpc';
import PostItemActions from './PostItemActions';
import PostItemBody from './PostItemBody';
import PostItemHead from './PostItemHead';
import PostViewTracker from './PostViewTracker';
import TopAnswerPreview from './TopAnswerPreview';
type Props = {
  spaceSlug?: string;
  post: Post;
  isFullLength: boolean;
  disableLink: boolean;
  isInView: boolean;
  isHeadless?: boolean;
  isThread?: boolean;
  disableTracking?: boolean;
  uniqueId?: string;
  minified?: boolean;
  disableReply?: boolean;
  onCardClick?: () => void;
  // Open the editor as soon as the post renders, for viewers allowed to edit
  // it. The board card detail modal sets this so opening a card you can edit
  // lands in the editor instead of asking for a second click on the body.
  // Board cards pass the list's factiiiId so the card hides that factiii from
  // its breakdown (redundant with the list it sits in).
  ignoreFactiiiId?: number;
};
const PostItem = props => {
  const $ = _c(84);
  const postRef = useRef(null);
  const {
    isVisible: postOnScreen
  } = useOnScreen(postRef);
  const router = useRouter();
  const pathname = usePathname();
  const {
    setBackNavigation
  } = useNavigator();
  const currentUser = useUserIdentity();
  const {
    joinPostStats,
    leavePostStats
  } = useSocket();
  let t0;
  if ($[0] !== props.post.postFactiiisCount) {
    t0 = isNsfw(props.post.postFactiiisCount);
    $[0] = props.post.postFactiiisCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [nsfw, setNsfw] = useState(t0);
  let t1;
  if ($[2] !== props.post.postFactiiisCount || $[3] !== props.post.space) {
    t1 = isPolitics(props.post.postFactiiisCount, props.post.space);
    $[2] = props.post.postFactiiisCount;
    $[3] = props.post.space;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [politics, setPolitics] = useState(t1);
  let t2;
  if ($[5] !== props.post.postFactiiisCount) {
    t2 = isTroll(props.post.postFactiiisCount);
    $[5] = props.post.postFactiiisCount;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const [troll, setTroll] = useState(t2);
  const [editOpen, setEditOpen] = useState(false);
  const isBoardLayoutSpace = props.post.pos != null;
  const isAuthor = props.post.user?.username === currentUser.username;
  let t3;
  if ($[7] !== currentUser.username || $[8] !== props.post.space) {
    t3 = ["space-membership", props.post.space, currentUser.username, "moderator-check"];
    $[7] = currentUser.username;
    $[8] = props.post.space;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props.post.space) {
    t4 = () => unwrapFromResult(client.spaces.getMembership.query({
      slug: props.post.space || ""
    })).then(_temp);
    $[10] = props.post.space;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = isBoardLayoutSpace && !isAuthor && Boolean(props.post.space) && Boolean(currentUser.isAuthenticated);
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      staleTime: 300000,
      enabled: t5
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    data: isBoardModerator
  } = useQuery(t6);
  const canEditCard = isBoardLayoutSpace && (isAuthor || Boolean(isBoardModerator));
  let t7;
  if ($[16] !== joinPostStats || $[17] !== leavePostStats || $[18] !== postOnScreen || $[19] !== props.post.id) {
    t7 = () => {
      if (postOnScreen) {
        joinPostStats(props.post.id);
      } else {
        leavePostStats(props.post.id);
      }
      return () => {
        leavePostStats(props.post.id);
      };
    };
    $[16] = joinPostStats;
    $[17] = leavePostStats;
    $[18] = postOnScreen;
    $[19] = props.post.id;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== postOnScreen || $[22] !== props.post.id) {
    t8 = [postOnScreen, props.post.id];
    $[21] = postOnScreen;
    $[22] = props.post.id;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  useEffect(t7, t8);
  const isHeadlessPost = props.isHeadless ?? false;
  const postPath = props.post.slug ? `/post/${props.post.slug}` : `/post/${props.post.id}`;
  const postHref = !props.post.isServerPrivate && props.post.isPrivate ? `/private-post/${props.post.id}` : postPath;
  let t9;
  if ($[24] !== pathname || $[25] !== postHref || $[26] !== props || $[27] !== router || $[28] !== setBackNavigation) {
    t9 = e => {
      if (props.disableLink) {
        props.onCardClick?.();
        return;
      }
      const target = e.target;
      if (target instanceof HTMLElement && !target.closest("#inputId")) {
        const factiiiPageMatch = pathname?.match(/^\/fids\/(\d+)/);
        let context;
        if (factiiiPageMatch) {
          const factiiiId = parseInt(factiiiPageMatch[1]);
          const matchingFactiii = props.post.postFactiiisCount.find(pfc => pfc.factiiiId === factiiiId);
          if (matchingFactiii) {
            context = matchingFactiii.name;
          }
        }
        setBackNavigation({
          path: pathname || "/",
          context
        });
        router.push(postHref);
      }
    };
    $[24] = pathname;
    $[25] = postHref;
    $[26] = props;
    $[27] = router;
    $[28] = setBackNavigation;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handlePostClick = t9;
  const t10 = `w-full grow rounded-lg border border-ds-border-subtle bg-background-light transition-all duration-300 ease-in-out md:mx-0 ${props.minified ? "p-3 hover:border-ds-border-hover hover:bg-background-light" : "p-4 hover:border-ds-border-hover hover:bg-background-light"} ${props.disableLink ? "" : "cursor-pointer"}`;
  const t11 = props.disableLink ? undefined : postHref;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setEditOpen(false);
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== editOpen || $[32] !== isHeadlessPost || $[33] !== nsfw || $[34] !== politics || $[35] !== props.ignoreFactiiiId || $[36] !== props.minified || $[37] !== props.post || $[38] !== props.spaceSlug || $[39] !== props.uniqueId || $[40] !== t11 || $[41] !== troll) {
    t13 = <PostItemHead spaceSlug={props.spaceSlug} post={props.post} politics={politics} nsfw={nsfw} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} ignoreFactiiiId={props.ignoreFactiiiId} uniqueId={props.uniqueId} isHeadless={isHeadlessPost} minified={props.minified} postHref={t11} externalEditOpen={editOpen} onExternalEditClose={t12} />;
    $[31] = editOpen;
    $[32] = isHeadlessPost;
    $[33] = nsfw;
    $[34] = politics;
    $[35] = props.ignoreFactiiiId;
    $[36] = props.minified;
    $[37] = props.post;
    $[38] = props.spaceSlug;
    $[39] = props.uniqueId;
    $[40] = t11;
    $[41] = troll;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const t15 = !props.minified;
  let t16;
  if ($[44] !== canEditCard || $[45] !== props.minified) {
    t16 = canEditCard && !props.minified ? () => setEditOpen(true) : undefined;
    $[44] = canEditCard;
    $[45] = props.minified;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== nsfw || $[48] !== politics || $[49] !== props.disableLink || $[50] !== props.isFullLength || $[51] !== props.isInView || $[52] !== props.minified || $[53] !== props.post || $[54] !== t15 || $[55] !== t16 || $[56] !== troll) {
    t17 = <PostItemBody isFullLength={props.isFullLength} post={props.post} politics={politics} nsfw={nsfw} disableLink={props.disableLink} isInView={props.isInView} troll={troll} ignorePostFactiiiIds={t14} minified={props.minified} fullBleedMedia={t15} onContentEditClick={t16} />;
    $[47] = nsfw;
    $[48] = politics;
    $[49] = props.disableLink;
    $[50] = props.isFullLength;
    $[51] = props.isInView;
    $[52] = props.minified;
    $[53] = props.post;
    $[54] = t15;
    $[55] = t16;
    $[56] = troll;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== props.isFullLength || $[59] !== props.post.topAnswer) {
    t18 = props.post.topAnswer && !props.isFullLength && <TopAnswerPreview answer={props.post.topAnswer} />;
    $[58] = props.isFullLength;
    $[59] = props.post.topAnswer;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const t19 = props.minified ? "mt-2 pt-2" : "mt-2 pt-3";
  let t20;
  if ($[61] !== nsfw || $[62] !== politics || $[63] !== props.disableReply || $[64] !== props.isThread || $[65] !== props.minified || $[66] !== props.post || $[67] !== props.uniqueId || $[68] !== troll) {
    t20 = <PostItemActions post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} redirectOnReply={props.isThread} uniqueId={props.uniqueId} minified={props.minified} disableReply={props.disableReply} />;
    $[61] = nsfw;
    $[62] = politics;
    $[63] = props.disableReply;
    $[64] = props.isThread;
    $[65] = props.minified;
    $[66] = props.post;
    $[67] = props.uniqueId;
    $[68] = troll;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t19 || $[71] !== t20) {
    t21 = <div className={t19}>{t20}</div>;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== handlePostClick || $[74] !== t10 || $[75] !== t13 || $[76] !== t17 || $[77] !== t18 || $[78] !== t21) {
    t22 = <div ref={postRef} data-post-card="true" className={t10} onClick={handlePostClick}>{t13}{t17}{t18}{t21}</div>;
    $[73] = handlePostClick;
    $[74] = t10;
    $[75] = t13;
    $[76] = t17;
    $[77] = t18;
    $[78] = t21;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  const postContent = t22;
  let t23;
  if ($[80] !== postContent || $[81] !== props.disableTracking || $[82] !== props.post) {
    t23 = <div>{!props.disableTracking || props.post.isPrivate ? <PostViewTracker post={props.post}>{postContent}</PostViewTracker> : postContent}</div>;
    $[80] = postContent;
    $[81] = props.disableTracking;
    $[82] = props.post;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  return t23;
};
export default PostItem;
function _temp(res) {
  return Boolean(res?.roles.includes(SpaceMemberRoles.MODERATOR)) || Boolean(res?.roles.includes(SpaceMemberRoles.OWNER));
}